import { Navigate, Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  LayoutDashboard, 
  Users, 
  Package, 
  Handshake, 
  Building2, 
  Wrench, 
  FileText,
  Upload,
  LogOut, 
  Menu, 
  X 
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { LOGO_URL } from '@/config/constants';

const AdminLayout = () => {
  const { admin, token, logout, loading } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#2a4599] border-t-transparent"></div>
      </div>
    );
  }

  if (!token) {
    return <Navigate to="/admin/login" replace />;
  }

  const navItems = [
    { name: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard },
    { name: 'Leads', path: '/admin/leads', icon: Users },
    { name: 'Packages', path: '/admin/packages', icon: Package },
    { name: 'Partners', path: '/admin/partners', icon: Handshake },
    { name: 'Listings', path: '/admin/listings', icon: Building2 },
    { name: 'Vendors', path: '/admin/vendors', icon: Wrench },
    { name: 'Collaboration', path: '/admin/collaboration', icon: Users },
    { name: 'Materials', path: '/admin/materials', icon: Upload },
    { name: 'Terms', path: '/admin/terms', icon: FileText },
  ];

  const isActive = (path) => location.pathname === path || (path === '/admin/dashboard' && location.pathname === '/admin');

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile sidebar toggle */}
      <button
        data-testid="admin-sidebar-toggle"
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-[#010822] text-white rounded-lg"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Sidebar */}
      <aside 
        className={`admin-sidebar fixed lg:static inset-y-0 left-0 z-40 w-64 transform transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="p-6">
          <Link to="/">
            <img 
              src={LOGO_URL} 
              alt="Alpha Groups" 
              className="h-12 w-auto object-contain"
            />
          </Link>
          <div className="mt-2 text-xs text-slate-400">Admin Portal</div>
        </div>

        <nav className="px-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`admin-nav-${item.name.toLowerCase()}`}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                isActive(item.path)
                  ? 'bg-white/10 text-white'
                  : 'text-slate-400 hover:bg-white/5 hover:text-white'
              }`}
            >
              <item.icon size={20} />
              <span className="font-medium text-sm">{item.name}</span>
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <div className="px-4 py-2 mb-2">
            <p className="text-white font-medium text-sm">{admin?.name}</p>
            <p className="text-slate-400 text-xs">{admin?.email}</p>
          </div>
          <Button
            data-testid="admin-logout"
            onClick={logout}
            variant="ghost"
            className="w-full justify-start gap-3 text-slate-400 hover:text-white hover:bg-white/5"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-0 min-h-screen">
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
